/**
 * Catches a social sign-in that quietly created a SECOND, EMPTY account (#4936).
 *
 * What happened to the customer who reported it: they bought a subscription on
 * the web under `jimbym@gmail.com`, then opened the iOS app, pressed "Sign in",
 * and used Sign in with Apple with Apple's "Hide My Email". Firebase does not
 * treat that as the same person — the address it hands us is a relay address
 * generated for this app, so it made a brand-new account that holds no
 * subscription. The app then showed them the Subscribe page. They read that as
 * "the app will not log me in" and wrote to support, and it took them sixteen
 * minutes to find their way onto the account they had paid for.
 *
 * The important limit, so that nobody later tries to remove the question this
 * file exists to ask: THE APP CANNOT WORK OUT THAT THE OTHER ACCOUNT EXISTS.
 * A relay address has no recoverable connection to the address the person
 * bought under, and the purchase was made in another app's built-in browser,
 * which shares no storage with us. There is nothing to look up. The only party
 * who knows about the other account is the customer, so the app asks them for
 * the address and only then can it check anything.
 *
 * There is NO Angular and NO Firebase in this file, so the unit-test runner can
 * load it — the same split `deep-link-signin.ts` uses. The Angular and Firebase
 * wiring lives in the login page. Keep any import here pure for the same reason;
 * `@cf-shared/email-normalization` is, so the runner still loads this file.
 */

import { normalizeEmail } from '@cf-shared/email-normalization';

/** What the app knows about a social sign-in that has just finished. */
export interface SocialSignInContext {
  /** True when the user pressed "Sign in", false when they pressed "Register". */
  userPressedSignIn: boolean;
  /**
   * True when the sign-in CREATED an account, false when it matched one that
   * already existed. Firebase reports this on the sign-in result.
   */
  accountWasJustCreated: boolean;
}

/** What asking the server about an address came back with. */
export type ExistingAccountLookupOutcome =
  /** The server answered. */
  | { kind: 'answered'; exists: boolean; ambiguous?: boolean }
  /** The check itself failed, so we know nothing either way. */
  | { kind: 'failed' };

/** What the app should tell the user after checking the address they gave. */
export type ExistingAccountVerdict =
  /** One account holds it — email a sign-in link there. */
  | 'send-link'
  /** Nothing holds it. */
  | 'no-account'
  /** Several accounts hold it, so no link can name the right one (#4505). */
  | 'ambiguous'
  /** We could not find out. */
  | 'lookup-failed';

/**
 * Decides whether to ask the user about another account they might own.
 *
 * Both conditions have to hold, and each one is what keeps this from becoming
 * noise. Pressing "Register" and getting a new account is exactly what that
 * button does, so there is nothing to report; and a sign-in that matched an
 * account that already existed is the ordinary returning user. Only the
 * combination — I asked to sign IN, and a new account was made for me — is a
 * contradiction of what the person asked for, and that combination is the bug.
 */
export function shouldOfferExistingAccountRecovery(ctx: SocialSignInContext): boolean {
  return ctx.userPressedSignIn && ctx.accountWasJustCreated;
}

/**
 * True when the address the user typed is the account they are already on.
 *
 * Worth its own step because the empty account DOES exist, so asking the server
 * about it comes back "yes, that account is there" and a link would be sent to
 * the very account they are stuck on. Comparison is done on the normalized
 * form: iOS capitalises the first letter of a text field by default, and people
 * paste addresses with a trailing space, so a plain `===` reads one address as
 * two. It normalizes with the shared `normalizeEmail`, which is the one body of
 * that rule — the server answers the same account-identity question with it, so
 * writing a second copy here would let the two drift apart silently.
 *
 * A blank entry is never "the same account" — the caller treats blank as the
 * user changing their mind, and without this guard a blank entry would compare
 * equal to a missing signed-in address.
 */
export function isSameAccountAsSignedIn(
  typedEmail: string | null | undefined,
  signedInEmail: string | null | undefined,
): boolean {
  const typed = normalizeEmail(typedEmail ?? undefined);
  const signedIn = normalizeEmail(signedInEmail ?? undefined);
  if (!typed || !signedIn) return false;
  return typed === signedIn;
}

/**
 * Turns the server's answer into the one thing to tell the user.
 *
 * The order of the checks is load-bearing. `ambiguous` is asked about BEFORE
 * `exists`, because an ambiguous answer carries `exists: true` as well — the
 * accounts really are there, we just cannot say which is theirs. The server
 * then refuses to send a link at all (#4505), so reading that as "send-link"
 * would tell somebody to go and watch for an email that is never sent.
 *
 * A failed check is reported as a failure and never as "no such account".
 * Telling a paying customer that the account they bought under does not exist
 * is both untrue and alarming, and it is the answer they are least able to
 * argue with.
 */
export function classifyExistingAccountLookup(
  outcome: ExistingAccountLookupOutcome,
): ExistingAccountVerdict {
  if (outcome.kind === 'failed') return 'lookup-failed';
  if (outcome.ambiguous) return 'ambiguous';
  return outcome.exists ? 'send-link' : 'no-account';
}
